/**
 * howToContent — the caregiver "How-To" care library registry.
 *
 * Markdown is the source of truth: each guide lives as a `.md` file under
 * `src/content/how-tos/<language>/`, the same filename in every language, and is
 * imported raw at build time, so the copy that ships is byte-identical to the file
 * that was signed off. Nothing here edits guide text; this module only carries the
 * metadata the app needs to list, order, route to, and cross-link the guides —
 * per language, since a guide's title, short title and description are copy too.
 *
 * A language's guides are shown only when it has all of them: every file, and the
 * metadata for every one (`hasHowToContent`; the metadata is in
 * lib/howToGuideText.ts). Otherwise the reader gets English, and the language is not
 * offered at all (lib/languageGate.ts). English's guides are bundled; another
 * language's are fetched with the rest of it when it is chosen (i18n.ts).
 *
 * Distinct from `educationContent.ts` — that system explains the clinical
 * picture in a modal; this one teaches hands-on technique on a full page.
 */

import i18next from 'i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from './language';
import { GUIDE_FILES, HOW_TO_TEXT } from './howToGuideText';
import { isCompleteHowTo } from './languageGate';

/** English's guide files, `../content/how-tos/en/<file>.md` → its markdown. Bundled. */
const ENGLISH_MARKDOWN = import.meta.glob<string>('../content/how-tos/en/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/** Every other language's guide files, fetched on demand (`loadHowToContent`). */
const LAZY_MARKDOWN = import.meta.glob<string>(['../content/how-tos/*/*.md', '!../content/how-tos/en/*.md'], {
  query: '?raw',
  import: 'default',
});

/** The guide files loaded so far, `../content/how-tos/<language>/<file>.md` → its markdown. */
const MARKDOWN: Record<string, string> = { ...ENGLISH_MARKDOWN };

/**
 * Fetch `lang`'s guide files, if it has them. Resolves once they are in (at once for
 * English). i18n.ts calls this with the rest of a language when it is chosen.
 */
export async function loadHowToContent(lang: Language): Promise<void> {
  const prefix = `../content/how-tos/${lang}/`;
  const pending = Object.entries(LAZY_MARKDOWN).filter(([path]) => path.startsWith(prefix) && !(path in MARKDOWN));
  const texts = await Promise.all(pending.map(([, load]) => load()));
  pending.forEach(([path], i) => {
    MARKDOWN[path] = texts[i];
  });
}

export interface HowToGuide {
  /** URL segment — the source filename with its numeric prefix stripped. */
  slug: string;
  /** Full display title (matches the guide's own H1). */
  title: string;
  /** Compact label for inline callouts and prev/next navigation. */
  shortTitle: string;
  /** One-line summary for the library index cards. */
  description: string;
  /** The guide's raw markdown, verbatim. */
  markdown: string;
  /** 1-based position in the library (the source file's numeric prefix). */
  order: number;
}

function markdownFor(lang: Language, file: string): string | undefined {
  return MARKDOWN[`../content/how-tos/${lang}/${file}`];
}

/** True when `lang`'s guide files are loaded, every one, with the metadata for every guide. */
export function hasHowToContent(lang: Language): boolean {
  return isCompleteHowTo(lang, (file) => markdownFor(lang, file));
}

/** The languages whose guides are complete. */
export function howToLanguages(): Language[] {
  return SUPPORTED_LANGUAGES.filter(hasHowToContent);
}

/** The language to show the guides in: `lang` (default: the app's) if complete, else English. */
function guideLanguage(lang: string = i18next.language ?? DEFAULT_LANGUAGE): Language {
  return (howToLanguages() as string[]).includes(lang) ? (lang as Language) : DEFAULT_LANGUAGE;
}

/** The guides in a language (default: the app's current one; English where incomplete). */
export function howToGuides(lang?: string): HowToGuide[] {
  const language = guideLanguage(lang);
  const text = HOW_TO_TEXT[language]!;
  return GUIDE_FILES.map(({ slug, file, order }) => ({
    slug,
    ...text.guides[slug],
    markdown: markdownFor(language, file)!,
    order,
  }));
}

/** The English guides — the source every translation is made from. */
export const HOW_TO_GUIDES: HowToGuide[] = howToGuides(DEFAULT_LANGUAGE);

/** Guides in library order (the numbered order they were written in). */
export const howToGuidesInOrder = (lang?: string): HowToGuide[] =>
  howToGuides(lang).sort((a, b) => a.order - b.order);

/** Look a guide up by slug. Undefined for an unknown slug — callers render a
 *  not-found state rather than crashing the route. */
export function getHowToGuide(slug: string | undefined, lang?: string): HowToGuide | undefined {
  if (!slug) return undefined;
  return howToGuides(lang).find((g) => g.slug === slug);
}

/** The previous/next guide in library order, for in-guide navigation. */
export function adjacentHowToGuides(slug: string, lang?: string): {
  previous: HowToGuide | null;
  next: HowToGuide | null;
} {
  const ordered = howToGuidesInOrder(lang);
  const i = ordered.findIndex((g) => g.slug === slug);
  if (i === -1) return { previous: null, next: null };
  return {
    previous: ordered[i - 1] ?? null,
    next: ordered[i + 1] ?? null,
  };
}

/** The English cross-reference phrases (see HowToLanguageText.crossReferences). */
export const HOW_TO_CROSS_REFERENCES: Record<string, string> = HOW_TO_TEXT.en!.crossReferences;

/** Slug a cross-reference phrase points at, in the guides' language, or undefined. */
export function crossReferencedSlug(text: string, lang?: string): string | undefined {
  return HOW_TO_TEXT[guideLanguage(lang)]!.crossReferences[text.trim()];
}

/**
 * Where the library lives for the surface the reader is currently on.
 *
 * Care Pass delegates browse under `/care/:token/...` with no account, so the
 * same guides are mounted there unauthenticated. Deriving the base from the
 * current path means one callout component works on both surfaces without
 * every caller threading a token through.
 */
export function howToBasePath(pathname: string): string {
  const carePass = /^\/care\/([^/]+)/.exec(pathname);
  return carePass ? `/care/${carePass[1]}/how-to` : '/how-to';
}

/** Route to the library index for the current surface. */
export function howToIndexPath(pathname: string): string {
  return howToBasePath(pathname);
}

/** Route to a single guide for the current surface. */
export function howToGuidePath(slug: string, pathname: string): string {
  return `${howToBasePath(pathname)}/${slug}`;
}
